import { useRef, useState } from 'react';
import { useReportStore } from '@/entities/report';

/**
 * Backs the report header wrapper with its pinned and hovered visibility state. Reads isShowTabHeader from the report store, keeps a debounced local hover flag driven by pointer enter and leave, and exposes a pin toggle that flips the stored value.
 *
 * @returns An object carrying isShowTabHeader, isHoverTabHeader and the handleMouseEnter, handleMouseLeave and onClickPin handlers.
 */
const useReportHeaderWrapper = () => {
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const setIsShowTabHeader = useReportStore((s) => s.actions.setIsShowTabHeader);

  const [isHoverTabHeader, setIsHoverTabHeader] = useState(false);

  const hoverTimeout = useRef(null);

  /**
   * Reveals the collapsed report tab header when the pointer rests over its hover zone. Clears any pending hover timer and schedules setting the hover flag after a 100 ms debounce.
   *
   * @returns Nothing.
   */
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setIsHoverTabHeader(true);
    }, 100); // Debounce delay
  };

  /**
   * Hides the report tab header again once the pointer leaves its hover zone. Clears any pending hover timer and schedules clearing the hover flag after a 100 ms debounce.
   *
   * @returns Nothing.
   */
  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setIsHoverTabHeader(false);
    }, 100); // Debounce delay
  };

  /**
   * Pins or unpins the report tab header. Flips the stored isShowTabHeader value so the header either stays visible or collapses back to its hover zone.
   *
   * @returns Nothing.
   */
  const onClickPin = () => {
    setIsShowTabHeader((prev) => !prev);
  };

  return {
    isShowTabHeader,
    isHoverTabHeader,
    handleMouseEnter,
    handleMouseLeave,
    onClickPin,
  };
};

export default useReportHeaderWrapper;
